import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconArrow,
  IconCalendar,
  IconDocument,
  IconError,
  IconPatients,
  IconRefresh,
  IconStar,
} from '../components/icons';
import {
  MetricGrid,
  MetricTile,
} from '../components/ui';
import { reviewAPI } from '../services/api';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';

const ContentLibrary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeMenuId, setActiveMenuId] = useState(null);

  // Fetch approved campaigns then enrich with real feedback data from Supabase
  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await reviewAPI.list({ status: 'approved' }, user?.id || null);
      const reviews = response.reviews || [];
      setCampaigns(reviews);

      // Fetch real feedback from REST API for all campaign IDs
      if (reviews.length > 0) {
        const campaignIds = reviews
          .map((r) => r.campaign_id || r.review_id)
          .filter(Boolean);

        if (campaignIds.length > 0) {
          try {
            const { data: resp } = await api.get('/analytics/interactions/feedback', {
              params: { campaign_ids: campaignIds.join(',') },
            });
            const interactions = resp?.interactions ?? resp ?? [];
            // Build feedback map: campaign_id → { delivered, feedback_count, avg_rating }
            const map = {};
            interactions.forEach((row) => {
              const id = row.campaign_id;
              if (!map[id]) map[id] = { delivered: 0, feedback_count: 0, ratings: [] };
              map[id].delivered += 1;
              if (row.feedback_rating) {
                map[id].feedback_count += 1;
                map[id].ratings.push(row.feedback_rating);
              }
            });
            // Compute avg rating
            Object.keys(map).forEach((id) => {
              const r = map[id].ratings;
              map[id].avg_rating = r.length > 0
                ? (r.reduce((a, b) => a + b, 0) / r.length).toFixed(1)
                : null;
            });
            setFeedbackMap(map);
          } catch (_fbErr) {
            // Feedback enrichment is best-effort; don't break the page
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
      setError(err.userMessage || 'Failed to load campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
    // user is read inside, so it belongs in the deps. With an empty array the
    // callback captured the first render's user (null), so the library loaded
    // unscoped — every tenant's approved content — and never refetched once
    // sign-in resolved.
  }, [user]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const getChannelBadges = (contentType) => {
    if (contentType === 'voice_script') return ['Voice SMS'];
    if (contentType === 'ivr_flow') return ['AI Voice'];
    if (contentType === 'sms') return ['SMS'];
    if (contentType === 'whatsapp') return ['WhatsApp'];
    if (contentType === 'multi_channel') return ['SMS', 'WhatsApp'];
    return [contentType?.toUpperCase() || 'Campaign'];
  };

  // An approved item is not necessarily a delivered one. The backend now
  // records what delivery actually did, so the library can stop presenting
  // "approved" as if it meant "patients received this".
  const getDeliveryState = (campaign) => {
    const d = campaign?.metadata?.delivery;
    if (!d) return null;
    if (d.delivered) {
      return { ok: true, label: `Delivered${d.recipients_delivered ? ` to ${d.recipients_delivered}` : ''}` };
    }
    return { ok: false, label: 'Not delivered', detail: d.error };
  };

  const handleMenuClick = (e, id) => {
    e.stopPropagation();
    setActiveMenuId(activeMenuId === id ? null : id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <IconRefresh className="h-8 w-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <IconError className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
        <button
          onClick={fetchCampaigns}
          className="mt-3 flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
        >
          <IconRefresh className="h-4 w-4" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Library</h1>
          <p className="text-gray-600 mt-1">All approved campaigns and their delivery status</p>
        </div>
        <button
          onClick={fetchCampaigns}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <IconRefresh className="w-4 h-4" /> Refresh
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="bg-white p-16 rounded-lg border border-gray-200 text-center">
          <IconDocument className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No campaigns in library yet</p>
          <p className="text-gray-400 mt-2 text-sm">
            Approved campaigns will appear here once reviewed.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {campaigns.map((campaign) => {
            const id = campaign.campaign_id || campaign.review_id;
            const badges = getChannelBadges(campaign.content_type);
            const fb = feedbackMap[id];
            const isMenuOpen = activeMenuId === campaign.review_id;
            const delivery = getDeliveryState(campaign);

            return (
              <div
                key={campaign.review_id}
                className="bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all relative"
              >
                <div className="p-6">
                  {/* Top row: badges + status + menu */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex gap-2 flex-wrap">
                      {badges.map((badge, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        Approved
                      </span>
                      {/* Approved is a decision; delivered is an outcome. An
                          approval whose delivery failed used to look identical
                          to one that reached every patient. */}
                      {delivery && (
                        <span
                          title={delivery.detail || ''}
                          className={
                            delivery.ok
                              ? 'px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full'
                              : 'px-2.5 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full'
                          }
                        >
                          {delivery.label}
                        </span>
                      )}
                      {/* Three-dot menu */}
                      <div className="relative">
                        <button
                          onClick={(e) => handleMenuClick(e, campaign.review_id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="5" r="2" />
                            <circle cx="12" cy="12" r="2" />
                            <circle cx="12" cy="19" r="2" />
                          </svg>
                        </button>
                        {isMenuOpen && (
                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                            <button
                              onClick={() => { navigate(`/review/${campaign.review_id}`); setActiveMenuId(null); }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              View Details
                            </button>
                            <button
                              onClick={() => { navigate('/analytics'); setActiveMenuId(null); }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              View Analytics
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    {campaign.campaign_name || campaign.disease_name || 'Untitled Campaign'}
                  </h2>

                  {/* Campaign Details */}
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 flex items-center gap-1">
                        <IconDocument className="h-3.5 w-3.5" /> Health Topic
                      </span>
                      <span className="font-medium text-gray-900">
                        {campaign.disease_name || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 flex items-center gap-1">
                        <IconPatients className="h-3.5 w-3.5" /> Recipients
                      </span>
                      <span className="font-medium text-gray-900">
                        {campaign.recipients_count ?? '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 flex items-center gap-1">
                        <IconCalendar className="h-3.5 w-3.5" /> Approved
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatDate(campaign.updated_at || campaign.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Delivery & Feedback Metrics from Supabase */}
                  <MetricGrid className="mb-4 grid-cols-2">
                    <MetricTile
                      label="Interactions tracked"
                      value={fb ? fb.delivered : '—'}
                    />
                    <MetricTile
                      label="Feedback received"
                      value={fb ? fb.feedback_count : '—'}
                    />
                  </MetricGrid>

                  {/* Feedback Summary or placeholder */}
                  {fb && fb.feedback_count > 0 ? (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="text-xs text-gray-500 font-medium mb-1">
                        Avg. Rating
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-yellow-600">
                          {fb.avg_rating}
                        </span>
                        <IconStar className="h-4 w-4 text-amber-400" weight="fill" />
                        <span className="text-xs text-gray-400">
                          / 5 from {fb.feedback_count} response{fb.feedback_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-center">
                      <p className="text-xs text-gray-400">
                        No feedback data yet.{' '}
                        <button
                          onClick={() => navigate('/analytics')}
                          className="text-primary-600 hover:underline"
                        >
                          View Analytics
                        </button>
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer action */}
                <div className="px-6 pb-4">
                  <button
                    onClick={() => navigate(`/review/${campaign.review_id}`)}
                    className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View full review <IconArrow className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContentLibrary;
